<script lang="ts">
export default {
  name: 'DropdownMenu'
};
</script>
<script lang="ts" setup>
import type { VariantType } from '#components/elements/button';
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import ButtonIcon from '#components/elements/button/buttonIcon/ButtonIcon.vue';
import type { IconName } from '#components/icons/icon.model';
import { MenuButton, MenuItems, Menu as UiMenu } from '@headlessui/vue';
import type { PropType } from 'vue';
import { computed } from 'vue';

const props = defineProps({
  icon: { type: String as PropType<IconName>, required: false, default: undefined },
  position: {
    type: String as PropType<'left' | 'right'>,
    required: false,
    default: 'right'
  },
  variant: { type: String as PropType<VariantType>, default: 'tertiary' },
  rounded: { type: Boolean, required: false, default: true },
  fullWidth: { type: Boolean, default: false },
  testId: { type: String, required: false, default: undefined },
  disabled: { type: Boolean, required: false, default: false },
  popover: { type: String, required: false, default: undefined }
});

const classes = computed(() => ({
  '!w-full !max-w-full': props.fullWidth
}));
</script>

<template>
  <UiMenu as="div" class="relative inline-block text-left" :class="classes" :data-testid="testId">
    <MenuButton v-if="$slots.default" class="flex items-center gap-x-2" :class="classes" :disabled="disabled">
      <span class="sr-only">Open menu</span>
      <ButtonBase
        :icon-left="icon"
        icon-right="chevron-down"
        :variant="variant"
        :full-width="fullWidth"
        :rounded="rounded"
        :disabled="disabled"
        :popover-right-icon="popover"
      >
        <slot />
      </ButtonBase>
    </MenuButton>

    <MenuButton v-if="!$slots.default" class="flex items-center rounded-full" :disabled="disabled">
      <span class="sr-only">Open menu</span>
      <ButtonIcon :icon="icon || 'chevron-down'" size="small" :disabled="disabled" />
    </MenuButton>
    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <MenuItems
        :class="[
          'absolute z-10 w-full min-w-fit max-w-xs rounded-md bg-white shadow-lg ring-black ring-opacity-5 focus:outline-none',
          {
            'right-0 origin-top-right': position === 'left',
            'left-0 origin-top-left': position === 'right'
          },
          classes
        ]"
      >
        <div class="py-1" :class="classes">
          <slot name="items" />
        </div>
      </MenuItems>
    </Transition>
  </UiMenu>
</template>
